import React from "react";
import {Modal} from "../Modal";
import {useModalContext} from "../../contexts/ModalContext/context";
import {Typography} from "../../UI/Typography";
import {Box} from "../../UI/Box";
import {Button} from "../../UI/Button";
import {EMAIL_ADDRESS, PHONE_NUMBER} from "../../constants/general";
import {Link} from "../Link";
import EditorRemoveIcon from "@atlaskit/icon/glyph/editor/remove";
import Api from "../../services/Api";
import useAuth from "../../routes/login/AuthContext";
import {useSnackbar} from "../../hooks/useSnackbar";

interface UserRemovalModalProps {
    name: string
    userId: string
    onSuccess?: () => void
}

export const UserRemovalModal: React.FC<UserRemovalModalProps> = ({name, userId, onSuccess}) => {
    const {setIsOpen} = useModalContext();
    const { getAuthToken } = useAuth();
    const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();

    const onHide = () => {
        setIsOpen(false);
    };

    const handleRemove = async () => {
        try {
            const success = await Api.deleteUser(userId, await getAuthToken());

            if (success) {
                if (onSuccess) onSuccess();
                showSuccessSnackbar("User successfully removed");
                onHide();
            } else {
                showErrorSnackbar("An error occurred. Please try again");
            }
        }  catch (error) {
            showErrorSnackbar("An error occurred. Please try again");
        }
    };

    return <Modal
        title="Confirm User Deletion"
        actions={
            <>
                <Button appearance="subtle" onClick={onHide}>
                    Cancel
                </Button>
                <Button appearance="danger" onClick={handleRemove} iconBefore={<EditorRemoveIcon label="Trash" />}>
                    Delete User
                </Button>
            </>
        }>
        <Box px={3} py={2}>
            <Typography variant="body2" gutterBottom>
                You are about to delete this user. This user will no longer have access to their memberships in
                RoomSync.
            </Typography>
            <Typography variant="body2" gutterBottom>
                If you have questions about how this tool works, please call us at
                {PHONE_NUMBER} or e-mail <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link>
            </Typography>
            <Typography variant="body2" gutterBottom>
                Delete User of: {name}
            </Typography>
        </Box>
    </Modal>;
};